import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { STRIPE } from "../../../../constants";
import DropdownCheckboxGroup from "../../../../parts/inputs/checkbox-group-dropdown";
import Select from "../../../../parts/inputs/select";
import Modal from "../../../../parts/modal-new";
import ModalFooter from "../../../../parts/modals/ModalFooter";
import OptionToggler from "../../../../parts/OptionToggler";
import ToolTip from "../../../../parts/tooltip";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { getConversationLabelColor } from "../../../../utils/chat-util";
import {
  clampIntervalCount,
  getPlanSummary,
} from "../../../../utils/plan-pricing";
import { PlanSummaryRail } from "./PlanSummaryRail";

const { SUPPORTED_CURRENCIES, ZERO_DECIMAL_CURRENCIES } = STRIPE;

const CUSTOM = "custom";
const NAMED_INTERVALS = ["day", "week", "month", "year"];
const RECURRING = "recurring";
const ONE_OFF = "one_off";

/**
 * Which segment an existing plan's cadence belongs to: a named interval when
 * it bills every one of them, "custom" for anything else (every 4 weeks, the
 * legacy quarter, …).
 */
const getIntervalMode = (recurring) => {
  if (!recurring) return "month";
  const { interval, interval_count } = recurring;
  // "day" is no longer a quick-select option (Stripe per-transaction fees on
  // daily billing eat most of the payment); a legacy day-interval plan falls
  // back to Custom so the segmented control always has a valid selection.
  if (interval === "day") return CUSTOM;
  if (interval_count === 1 && NAMED_INTERVALS.includes(interval)) {
    return interval;
  }
  return CUSTOM;
};

export const CreatePlan = ({
  plan = {},
  type,
  user,
  taxRates,
  planPricing,
}) => {
  const { t } = useTranslation("coaching-portal");
  const { id, nickname, metadata, unit_amount, recurring, currency } = plan;
  const { pressCustomBranding = {} } = user;
  const { gymLocations = [] } = pressCustomBranding;
  const isEdit = !!id;

  const [features, setFeatures] = useState(
    plan.features?.length ? plan.features : [""]
  );
  const [locations, setLocations] = useState(
    plan.facilikey?.length ? plan.facilikey : []
  );

  const [selectFormValues, setSelectFormValues] = useState({
    currency: currency || SUPPORTED_CURRENCIES[0],
    access: metadata?.access || "pull",
    text_rate: metadata?.tax_rate || "",
  });
  const handleSelectChange = ({ name, value }) => {
    setSelectFormValues({
      ...selectFormValues,
      [name]: value,
    });
  };

  // PRESS sells recurring memberships and one-off passes from the same modal.
  // An existing plan's type is read off its price: Stripe one-time prices have
  // no `recurring` object.
  const [billingType, setBillingType] = useState(
    isEdit && !recurring ? ONE_OFF : RECURRING
  );
  const isOneOff = type === "press" && billingType === ONE_OFF;
  const [passType, setPassType] = useState(metadata?.pass_type || "day");
  const isDayPass = isOneOff && passType === "day";

  // Billing cadence. `intervalMode` drives the segmented control; the interval
  // and count actually submitted are derived from it, so "Custom" is purely a
  // UI state and never reaches the API.
  const [intervalMode, setIntervalMode] = useState(getIntervalMode(recurring));
  const [customInterval, setCustomInterval] = useState(
    recurring?.interval &&
      recurring.interval !== "day" &&
      NAMED_INTERVALS.includes(recurring.interval)
      ? recurring.interval
      : "week"
  );
  const [customCount, setCustomCount] = useState(
    recurring?.interval_count > 1 ? String(recurring.interval_count) : ""
  );
  const [price, setPrice] = useState(
    unit_amount
      ? ZERO_DECIMAL_CURRENCIES.includes((currency || "").toUpperCase())
        ? String(unit_amount)
        : String(unit_amount / 100)
      : ""
  );

  const isCustom = intervalMode === CUSTOM;
  const interval = isCustom ? customInterval : intervalMode;
  const intervalCount = isCustom ? customCount || 1 : 1;

  const summary = useMemo(
    () =>
      getPlanSummary(
        {
          product: type,
          price,
          currency: selectFormValues.currency,
          interval,
          intervalCount,
          billingType: isOneOff ? ONE_OFF : RECURRING,
        },
        planPricing
      ),
    [
      type,
      price,
      selectFormValues.currency,
      interval,
      intervalCount,
      isOneOff,
      planPricing,
    ]
  );

  // Below floor is a hard stop: plans under the minimum have to be created in
  // Stripe directly, so the modal refuses to submit one. The API re-checks.
  // Only on creation, though — floors are not applied retroactively, and an
  // existing plan's price and cadence are immutable, so blocking save here
  // would leave a grandfathered plan's name and features uneditable forever.
  // The rail still shows the warning; the API's PUT path does not floor-check.
  const belowFloor = summary.below && !isEdit;

  // The form posts natively (rill's global submit handler fetches + swaps
  // the DOM) — nothing there stops a second click from firing its own
  // submit event before the first request resolves, which duplicated the
  // plan. Guard with a ref (synchronous, unlike state) so a second submit
  // event arriving before React re-renders is still blocked.
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleFormSubmit = (e) => {
    if (isSubmittingRef.current) {
      e.preventDefault();
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);
  };
  // A failed submit re-renders this same modal in place (Rill's error
  // handler redirects back to the referrer, no navigation away) rather than
  // unmounting it, so the ref/state above never got a natural reset and Save
  // stayed disabled until the modal was closed and reopened. `req` gets a
  // new identity on every finished request cycle, success or failure, so it
  // doubles as a "the in-flight submit is done" signal here.
  const { req } = useRillContext();
  useEffect(() => {
    if (req && isSubmittingRef.current) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [req]);

  const handleCheck = (e) => {
    const { checked, value } = e.target;
    setLocations((prevState) => {
      if (!checked) {
        return prevState.filter((location) => location !== value);
      }
      return [...prevState, value];
    });
  };

  const locationsWithKeys = gymLocations.filter((loc) => !!loc.facilikey);
  const checkboxes = locationsWithKeys?.length
    ? locationsWithKeys.map(({ facilikey, gymName, gymLocation }) => ({
        value: facilikey,
        title: `${gymName} (${gymLocation})`,
      }))
    : null;

  const inputRefs = useRef([]);
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Skip on mount: focusing a feature input this early scrolls the modal
    // to it, hiding the fields above (worst on "press", which has the most
    // rows before the features list). Still focus on every later add.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Focus the last input
    if (inputRefs.current.length > 0) {
      const lastInput = inputRefs.current[inputRefs.current.length - 1];
      if (lastInput && lastInput.value === "") lastInput.focus();
    }
  }, [features]);
  const handleRemoveFeature = (index) => {
    setFeatures((prevState) => {
      const newState = prevState.filter((_, idx) => idx !== index);
      inputRefs.current = inputRefs.current.filter((_, idx) => idx !== index);
      return newState;
    });
  };
  const typeLabelColor = getConversationLabelColor(type.toUpperCase());
  return (
    <Modal
      className={`plan-modal ${type}`}
      title={
        isEdit
          ? t("planManager.createPlan.editPlan")
          : t("planManager.createPlan.addNewPlan")
      }
      headerStart={
        <span
          className="plan-type-badge"
          style={{
            border: `1px solid ${typeLabelColor.color}`,
            color: typeLabelColor.color,
            backgroundColor: typeLabelColor.background,
          }}
        >
          {type}
        </span>
      }
      toClear={["plan", "type"]}
    >
      <form
        className="body"
        action="/actions/plan-manager/save"
        method="POST"
        data-noreset
        id="create-plan"
        onSubmit={handleFormSubmit}
      >
        {isEdit && <input type="hidden" name="id" value={id} />}
        <input type="hidden" name="type" value={type} />
        <div className="plan-fields">
          {type === "press" && (
            <div className="row billing-type">
              <div className="group">
                <label htmlFor="billing_type" className="label req">
                  {t("planManager.createPlan.billingType")}
                </label>
                <OptionToggler
                  showRadio
                  name="billing_type"
                  defaultValue={billingType}
                  disabled={isEdit}
                  onSelectFn={setBillingType}
                  options={[
                    {
                      value: RECURRING,
                      label: t("planManager.createPlan.recurring"),
                    },
                    {
                      value: ONE_OFF,
                      label: t("planManager.createPlan.oneOff"),
                    },
                  ]}
                />
              </div>
            </div>
          )}
          <div className="row name-row">
            <div className="group">
              <label htmlFor="nickname" className="label req">
                {t("planManager.createPlan.name")}
              </label>
              <input
                type="text"
                name="nickname"
                placeholder={t("planManager.createPlan.enterPlanName")}
                required
                defaultValue={nickname || ""}
              />
            </div>
          </div>
          <div className="row">
            <div className="group">
              <label htmlFor="price" className="label req">
                {t("planManager.createPlan.price")}
              </label>
              <input
                type="text"
                inputMode="decimal"
                name="price"
                placeholder={t("planManager.createPlan.enterPlanPrice")}
                required
                disabled={isEdit}
                value={price}
                onChange={(e) => {
                  // type="number" silently reports an empty `.value` (and
                  // submits nothing) for a mid-edit string like "10." even
                  // though the field still visibly shows it — sc-45001.
                  // Plain text + manual digit/decimal filtering keeps what's
                  // typed and what's submitted always in sync.
                  const val = e.target.value;
                  if (/^\d*\.?\d*$/.test(val)) setPrice(val);
                }}
              />
            </div>
            <div className="group">
              <label htmlFor="currency" className="label req">
                {t("planManager.createPlan.currency")}
              </label>

              <Select
                name="currency"
                id="currency"
                overlay
                disabled={isEdit}
                options={SUPPORTED_CURRENCIES.map((currency) => {
                  return {
                    value: currency.toLowerCase(),
                    text: currency,
                  };
                })}
                value={selectFormValues.currency}
                onChange={(newVal) =>
                  handleSelectChange({ name: "currency", value: newVal })
                }
              />
            </div>
          </div>
          {/* A one-off pass is a Stripe one-time price — it has no cadence, so
              the interval control goes away entirely. */}
          {!isOneOff && (
            <div className="row billing-interval">
              <div className="group">
                <label htmlFor="interval_mode" className="label req">
                  {t("planManager.createPlan.billingInterval")}
                </label>
                {/* The segmented control is UI state only — the API is sent the
                derived interval + interval_count in the hidden inputs below. */}
                <OptionToggler
                  showRadio
                  name="interval_mode"
                  defaultValue={intervalMode}
                  disabled={isEdit}
                  onSelectFn={setIntervalMode}
                  options={[
                    { value: "week", label: t("planManager.createPlan.week") },
                    {
                      value: "month",
                      label: t("planManager.createPlan.month"),
                    },
                    { value: "year", label: t("planManager.createPlan.year") },
                    {
                      value: CUSTOM,
                      label: t("planManager.createPlan.custom"),
                    },
                  ]}
                />
                <input type="hidden" name="interval" value={interval} />
                <input
                  type="hidden"
                  name="interval_count"
                  value={intervalCount}
                />
              </div>
            </div>
          )}
          {isCustom && !isOneOff && (
            <div className="row custom-interval">
              <label htmlFor="custom_count" className="label">
                {t("planManager.createPlan.every")}
              </label>
              <div className="group">
                <input
                  type="number"
                  id="custom_count"
                  placeholder={t("planManager.createPlan.enterNumber")}
                  min={1}
                  max={planPricing?.intervalCaps?.[customInterval] ?? 1}
                  step={1}
                  required
                  disabled={isEdit}
                  value={customCount}
                  onChange={(e) => setCustomCount(e.target.value)}
                  onBlur={(e) => {
                    if (!e.target.value) return;
                    // Stripe rejects a billing span over a year, so hold the
                    // operator inside the cap rather than failing on save.
                    setCustomCount(
                      String(
                        clampIntervalCount(
                          customInterval,
                          e.target.value,
                          planPricing?.intervalCaps
                        )
                      )
                    );
                  }}
                />
              </div>
              <div className="group">
                <Select
                  id="custom_interval"
                  overlay
                  value={customInterval}
                  disabled={isEdit}
                  onChange={setCustomInterval}
                  options={[
                    { value: "week", text: t("planManager.createPlan.weeks") },
                    {
                      value: "month",
                      text: t("planManager.createPlan.months"),
                    },
                    { value: "year", text: t("planManager.createPlan.years") },
                  ]}
                />
              </div>
            </div>
          )}
          {isOneOff && (
            <div className="row pass-type">
              <div className="group">
                <label htmlFor="pass_type" className="label req">
                  {t("planManager.createPlan.passType")}
                </label>
                <OptionToggler
                  showRadio
                  name="pass_type"
                  defaultValue={passType}
                  disabled={isEdit}
                  onSelectFn={setPassType}
                  options={[
                    {
                      value: "day",
                      label: t("planManager.createPlan.dayPass"),
                    },
                    {
                      value: "week",
                      label: t("planManager.createPlan.weekPass"),
                    },
                  ]}
                />
              </div>
            </div>
          )}
          {isDayPass && (
            <div className="row day-instructions">
              <div className="group">
                <div className="label-with-hint">
                  <label htmlFor="custom_instructions" className="label req">
                    {t("planManager.createPlan.entryInstructions")}
                  </label>
                  <ToolTip
                    id="entry-instructions-hint"
                    body={t("planManager.createPlan.entryInstructionsHint")}
                  >
                    <img src="/img/new-icons/info-circle.svg" alt="" />
                  </ToolTip>
                </div>
                <textarea
                  id="custom_instructions"
                  name="custom_instructions"
                  rows={3}
                  maxLength={500}
                  required
                  disabled={isEdit}
                  placeholder={t(
                    "planManager.createPlan.entryInstructionsPlaceholder"
                  )}
                  defaultValue={metadata?.custom_instructions || ""}
                />
              </div>
            </div>
          )}
          {/* On a day pass the tax rate is all that is left of this row. */}
          {type === "press" && (!isDayPass || taxRates?.length > 0) && (
            <div className="row flex-col gap-6">
              {/* A day pass creates no account and no key, so the access model
                  and the key-issuing gym locations have nothing to act on. */}
              {!isDayPass && (
                <div className="group">
                  <label htmlFor="access" className="label req">
                    {t("planManager.createPlan.userAccess")}
                  </label>
                  <Select
                    name="access"
                    id="access"
                    overlay
                    disabled={isEdit}
                    value={selectFormValues.access}
                    onChange={(newVal) =>
                      handleSelectChange({ name: "access", value: newVal })
                    }
                    options={[
                      {
                        text: t("planManager.createPlan.pullAccess"),
                        value: "pull",
                      },
                      {
                        text: t("planManager.createPlan.pushAccess"),
                        value: "push",
                      },
                    ]}
                  />
                  {/* The raw Pull/Push labels do not say what the member gets,
                      so the selected model describes itself here and again in
                      the summary rail. */}
                  <p className="field-help">
                    {t(
                      `planManager.createPlan.accessModels.${selectFormValues.access}.description`
                    )}
                  </p>
                </div>
              )}
              {taxRates?.length > 0 && (
                <div className="group">
                  <label htmlFor="tax_rate" className="label req">
                    {t("planManager.createPlan.taxRate")}
                  </label>
                  <Select
                    name="tax_rate"
                    id="tax_rate"
                    overlay
                    value={selectFormValues.text_rate}
                    onChange={(newVal) =>
                      handleSelectChange({ name: "text_rate", value: newVal })
                    }
                    options={[
                      ...taxRates.map(({ id, display_name, percentage }) => {
                        return {
                          text: `${display_name} - ${percentage}%`,
                          value: id,
                        };
                      }),
                      { text: t("planManager.createPlan.none"), value: "" },
                    ]}
                  />
                </div>
              )}
              {checkboxes && !isDayPass && (
                <div className="group">
                  <DropdownCheckboxGroup
                    label={t("planManager.createPlan.gymLocationKeyAccess")}
                    name="facilikey"
                    onChange={(e) => handleCheck(e)}
                    checkboxes={checkboxes}
                    checkboxClass="gym-locations"
                    values={locations}
                  />
                </div>
              )}
            </div>
          )}

          {type === "press" && (
            <div className="features-list">
              <div className="group">
                <label htmlFor="feature_1" className="label req">
                  {t("planManager.createPlan.included")}
                </label>
                {features.map((value, i) => {
                  return (
                    <div className="input-row" key={`row${i}`}>
                      <input
                        type="text"
                        name={`feature_${i + 1}`}
                        placeholder={t(
                          "planManager.createPlan.enterPlanFeatures"
                        )}
                        value={value}
                        required
                        ref={(el) => (inputRefs.current[i] = el)}
                        onChange={(e) => {
                          e.preventDefault();
                          const value = e.target.value;
                          setFeatures((prevState) => {
                            const newState = [...prevState];
                            newState[i] = value;
                            return newState;
                          });
                        }}
                        onKeyDown={(e) => {
                          const lastInput =
                            inputRefs.current[inputRefs.current.length - 1];
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            if (lastInput?.value !== "")
                              setFeatures((prevState) => prevState.concat(""));
                          }
                        }}
                      />

                      {i !== 0 ? (
                        <button
                          data-button-type="text-white"
                          data-icon-only
                          onClick={(e) => {
                            e.preventDefault();
                            if (e.key !== "Enter") {
                              handleRemoveFeature(i);
                            }
                          }}
                          type="button"
                        >
                          <img
                            src="/img/new-icons/red-trash.svg"
                            alt="delete"
                          />
                        </button>
                      ) : (
                        <button
                          disabled
                          style={{ cursor: "default" }}
                          type="button"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <button
                data-button-type="outline"
                onClick={(e) => {
                  e.preventDefault();
                  setFeatures((prevState) => prevState.concat(""));
                }}
                disabled={
                  features.length >= 10 ||
                  !features[features.length - 1]?.length > 0
                }
                type="button"
              >
                <span>{t("planManager.createPlan.addFeature")}</span>
              </button>
            </div>
          )}
        </div>
        <PlanSummaryRail
          summary={summary}
          currency={selectFormValues.currency}
          price={price}
          interval={interval}
          intervalCount={intervalCount}
          accessModel={
            type === "press" && !isDayPass ? selectFormValues.access : null
          }
          passType={isOneOff ? passType : null}
        />
      </form>
      <ModalFooter
        isSubmitButton
        showAltButton={false}
        onClickButton={() => {}}
        classNameButton="button save"
        formId="create-plan"
        disabled={belowFloor || isSubmitting}
        buttonText={
          belowFloor ? t("planManager.createPlan.saveBelowFloor") : undefined
        }
      />
    </Modal>
  );
};
